import draugasAugustPreview from "./assets/draugas-2013-08-24-preview.webp";
import draugasSeptemberPreview from "./assets/draugas-2013-09-28-preview.webp";
import publication2001Preview from "./assets/publication-2001-preview.webp";
import { publicMedia } from "./generatedMedia";
import type { Locale } from "./i18n";

export type Category =
  | "ceramics"
  | "painting"
  | "drawings"
  | "fluxus"
  | "other";
export type Localized = Partial<Record<Locale, string>>;
export type MediaAsset = {
  id: string;
  fullUrl?: string;
  previewUrl?: string;
  pdfUrl?: string;
  originalUrl?: string;
  alt: Localized;
};
export type Artwork = {
  id: string;
  slug: string;
  category: Category;
  title: Localized;
  description?: Localized;
  exhibitionLink?: { url: string; label: Localized };
  year?: string;
  technique?: Localized;
  materials?: Localized;
  dimensions?: Localized;
  firingTemperature?: string;
  owner?: Localized;
  relatedProject?: string;
  coverMediaId: string;
  mediaIds: string[];
  publicationState: "published" | "draft";
  metadataReviewState: "complete" | "partial";
};

// Explicit local-preview and authorized direct-ICP builds bundle curated WebPs.
// Cloud/default builds retain unset URLs pending an authorized persistent origin.
declare const __BUNDLED_ARTWORK_PREVIEW__: boolean;
export const mediaAssets: MediaAsset[] = publicMedia.map((item) => ({
  id: item.id,
  fullUrl: __BUNDLED_ARTWORK_PREVIEW__
    ? `/media/large/${item.id}.webp`
    : undefined,
  previewUrl: __BUNDLED_ARTWORK_PREVIEW__
    ? `/media/thumb/${item.id}.webp`
    : undefined,
  pdfUrl:
    __BUNDLED_ARTWORK_PREVIEW__ && "pdfPath" in item
      ? `/media/${item.pdfPath}`
      : undefined,
  originalUrl:
    __BUNDLED_ARTWORK_PREVIEW__ && "originalPath" in item
      ? `/media/${item.originalPath}`
      : undefined,
  alt: {
    lt:
      "sourceFilename" in item
        ? item.sourceFilename
        : "Aušros Mačiulytės kūrinio vaizdas",
    en:
      "sourceFilename" in item
        ? item.sourceFilename
        : "Artwork by Aušra Mačiulytė",
  },
}));
const mediaById = new Map(mediaAssets.map((media) => [media.id, media]));
// These already-mapped photos show the art itself instead of exhibition posters.
// Source membership is documented in docs/ARTWORK_MEDIA_MAPPING.md.
const localCoverMediaIds: Readonly<Record<string, string>> = {
  "perkuno-zirgai": "m-2fd3a5bb04b57bd4",
  pasakeles: "m-523b056ce089615f",
};
// Each batch below was individually tied to a named work or named series by
// docs/ARTWORK_CATALOG.md. Batches elsewhere in the archive are not published.
function mediaForBatches(...batches: string[]): string[] {
  // Preserve batch order when appending related images to an existing gallery.
  return batches.flatMap((batch) =>
    publicMedia
      .filter((media) => media.batchId === batch)
      .map((media) => media.id),
  );
}
function artwork(
  slug: string,
  category: Category,
  title: Localized,
  batches: string[],
  details: Partial<
    Omit<
      Artwork,
      | "id"
      | "slug"
      | "category"
      | "title"
      | "coverMediaId"
      | "mediaIds"
      | "publicationState"
    >
  > = {},
): Artwork {
  const mediaIds = mediaForBatches(...batches);
  if (mediaIds.length === 0)
    throw new Error(`No published media for artwork ${slug}`);
  return {
    id: slug,
    slug,
    category,
    title,
    mediaIds,
    coverMediaId: __BUNDLED_ARTWORK_PREVIEW__
      ? (localCoverMediaIds[slug] ?? mediaIds[0])
      : mediaIds[0],
    publicationState: "published",
    metadataReviewState: "partial",
    ...details,
  };
}
// Source: am_web/mail_text/2026-09-24-15h13m52s-P2Il--R--3-9-.txt.
// Supporting ephemera belong on About, not in Works or Articles.
export const vjDruskaArchiveMedia = mediaForBatches("b-4ea59707eb43").flatMap(
  (id) => {
    const media = mediaById.get(id);
    return media ? [media] : [];
  },
);
export const vjDruskaArchivePdfUrl = __BUNDLED_ARTWORK_PREVIEW__
  ? "/media/documents/vj-druska-archive.pdf"
  : undefined;
export const artworks: Artwork[] = [
  artwork(
    "sakralus-sanciu-pastas",
    "ceramics",
    { lt: "Sakralus Šančių paštas", en: "Sacred Šančiai Post" },
    ["b-89bbfb668eea"],
    {
      year: "2018",
      technique: { lt: "Keramika", en: "Ceramics" },
      materials: { lt: "Mišrus šamotinis molis", en: "Mixed grogged clay" },
      description: {
        lt: "Kaunas, Vilnų g. 17. Adresas ir istorija — kopija to, kas neša žinią ir buvo tikrinama kiekvieną kartą praeinant pro šalį. Molis turi atmintį: objektų detalės ir vieno namo istorija jose. Atmintis, išliekanti įspauduose.",
        en: "Kaunas, Vilnų St. 17. An address and a history — a copy of what carries a message and was checked each time one passed by. Clay has a memory: details of objects and the history of one house within them. Memory remaining in impressions.",
      },
    },
  ),
  artwork(
    "duona",
    "ceramics",
    { lt: "Duona", en: "Bread" },
    ["b-bb5c0141d85f"],
    {
      year: "2022",
      technique: { lt: "Keramika", en: "Ceramics" },
      materials: { lt: "Angoba, glazūra", en: "Engobe, glaze" },
    },
  ),
  artwork(
    "miskas",
    "ceramics",
    { lt: "Miškas", en: "Forest" },
    ["b-5bafdc9d3bf3"],
    {
      year: "2022",
      technique: { lt: "Skulptūra, triptikas", en: "Sculptural triptych" },
      materials: { lt: "Šamotas", en: "Grogged clay" },
      dimensions: {
        lt: "86 × 50 cm; 97 × 50 cm; 88 × 50 cm",
        en: "86 × 50 cm; 97 × 50 cm; 88 × 50 cm",
      },
      description: {
        lt: "Zarasų kultūros centro patalpose. Čia viskas apie naktį miške, kada šešėlis tampa svarbesnis už tikrovę. Skulptūros krentantys šešėliai rodo apie mišką daugiau nei pati skulptūra. Ji netikra — šešėliai atverčia ją.",
        en: "At Zarasai Cultural Centre. This is about a night in the forest, when shadow becomes more important than reality. The sculpture's shadows reveal more about the forest than the sculpture itself. It is unreal — the shadows turn it over.",
      },
    },
  ),
  artwork(
    "karvute",
    "ceramics",
    { lt: "Karvutė", en: "Little Cow" },
    ["b-5975cc409c7f"],
    {
      year: "2023",
      technique: { lt: "Keramika", en: "Ceramics" },
      materials: {
        lt: "Šamotas, angoba, glazūra",
        en: "Grogged clay, engobe, glaze",
      },
      dimensions: { lt: "24 × 12 × 14 cm", en: "24 × 12 × 14 cm" },
      firingTemperature: "1050 °C",
      owner: {
        lt: "Baltriškių vienuolyno nuosavybė",
        en: "Property of Baltriškės Monastery",
      },
    },
  ),
  artwork(
    "asilas",
    "ceramics",
    { lt: "Asilas", en: "Donkey" },
    ["b-38a7827c8e41"],
    {
      year: "2023",
      technique: { lt: "Keramika", en: "Ceramics" },
      materials: { lt: "Angoba, glazūra", en: "Engobe, glaze" },
      firingTemperature: "1100 °C",
      owner: {
        lt: "Baltriškių vienuolyno nuosavybė",
        en: "Property of Baltriškės Monastery",
      },
    },
  ),
  artwork(
    "marija",
    "ceramics",
    { lt: "Marija", en: "Mary" },
    ["b-d1e53c258cf0"],
    {
      year: "2023",
      technique: { lt: "Keramika", en: "Ceramics" },
      materials: {
        lt: "Šamotas, angoba, glazūra",
        en: "Grogged clay, engobe, glaze",
      },
      firingTemperature: "1100 °C",
      owner: {
        lt: "Baltriškių vienuolyno nuosavybė",
        en: "Property of Baltriškės Monastery",
      },
    },
  ),
  artwork(
    "juozapas",
    "ceramics",
    { lt: "Juozapas", en: "Joseph" },
    ["b-99416d8b6a36"],
    {
      year: "2023",
      technique: { lt: "Keramika", en: "Ceramics" },
      materials: {
        lt: "Šamotas, angoba, glazūra",
        en: "Grogged clay, engobe, glaze",
      },
      firingTemperature: "1100 °C",
      owner: {
        lt: "Baltriškių vienuolyno nuosavybė",
        en: "Property of Baltriškės Monastery",
      },
    },
  ),
  artwork(
    "kristus",
    "ceramics",
    { lt: "Kristus", en: "Christ" },
    ["b-4fff1693013e"],
    {
      year: "2023",
      technique: { lt: "Keramika", en: "Ceramics" },
      materials: {
        lt: "Šamotas, angoba, glazūra",
        en: "Grogged clay, engobe, glaze",
      },
      firingTemperature: "1100 °C",
      owner: {
        lt: "Baltriškių vienuolyno nuosavybė",
        en: "Property of Baltriškės Monastery",
      },
    },
  ),
  artwork(
    "trys-karaliai",
    "ceramics",
    { lt: "Trys karaliai", en: "Three Kings" },
    ["b-f2d5e508f8c4"],
    {
      year: "2023",
      technique: { lt: "Keramika", en: "Ceramics" },
      materials: {
        lt: "Šamotas, angoba, glazūra",
        en: "Grogged clay, engobe, glaze",
      },
      firingTemperature: "1100 °C",
      owner: {
        lt: "Baltriškių vienuolyno nuosavybė",
        en: "Property of Baltriškės Monastery",
      },
    },
  ),
  artwork(
    "keliones-prisiminimai",
    "fluxus",
    { lt: "Kelionės prisiminimai", en: "Travel Memories" },
    ["b-eae074b8233c"],
    {
      technique: { lt: "Fluxus knygelė", en: "Fluxus booklet" },
      description: {
        lt: "Knygelė iš Herakliono, kurią galima sukurti tik iš to, kas randama kelionėje, o piešti tik tai, kas matoma esamu laiku.",
        en: "A booklet from Heraklion, made only from what is found on a journey and drawn only from what is visible in the present moment.",
      },
    },
  ),
  // Complete owner-supplied set; cover is first in its dedicated indexed batch.
  // Provenance: content/indexes/fluxus_travel_notes_index.json.
  artwork(
    "keliones-uzrasai-be-taisykliu",
    "fluxus",
    {
      lt: "Kelionės užrašai be taisyklių: Fluxus knygos iš pasaulio pakraščių",
      en: "Travel Notes Without Rules: Fluxus Books from the Edges of the World",
    },
    ["owner-fluxus-travel-notes"],
    {
      description: {
        lt: "Tai ne šiaip kelionių dienoraščiai – tai gyvi, taktiliniai pasakojimai, sukurti pagal „Fluxus“ filosofiją. Kiekvienas puslapis yra meninis eksperimentas, kuriamas iš priemonių, kurias Aušros Mačiulytės ūkyje kurianti autorė atranda tiesiog po kojomis ar kelionių kelyje: senų traukinio bilietų, pajūrio smėlio nugludintų popierėlių, sudžiovintų vietinių augalų lapų, vietinių laikraščių iškarpų ar pamestų smulkmenų. Šios knygos neturi linijų ar griežtos chronologijos. Jos fiksuoja ne lankytinas vietas, o akimirkos nuotaiką, kvapą ir atsitiktinumo grožį. Tai apčiuopiama patirtis, transformuojanti kelionės fragmentus į unikalų meno kūrinį.",
        en: "These are not just travel diaries – they are living, tactile narratives created in the spirit of the Fluxus philosophy. Each page is an artistic experiment, made from materials that the artist, who creates on Aušra Mačiulytė’s farm, discovers right beneath her feet or along the way on her travels: old train tickets, scraps of paper smoothed by seaside sand, dried leaves of local plants, clippings from local newspapers or small lost objects. These books have no ruled lines or strict chronology. They capture not tourist sights, but the mood and scent of a moment and the beauty of chance. This is a tangible experience that transforms fragments of a journey into a unique work of art.",
      },
    },
  ),
  artwork(
    "vasario-16-kovo-11",
    "painting",
    { lt: "Vasario 16 – Kovo 11", en: "February 16 – March 11" },
    ["b-ada782a8f529"],
    { year: "2020", technique: { lt: "Aliejus, drobė", en: "Oil on canvas" } },
  ),
  // Owner-requested single gallery; original images stay in MIME attachment order.
  // Source/filenames: content/indexes/selfportraits_2025_index.json.
  artwork(
    "autoportretai-2025",
    "painting",
    { lt: "Autoportretai", en: "Self-Portraits" },
    ["b-13ffbc87599e"],
    {
      year: "2025",
      technique: { lt: "Aliejus, drobė", en: "Oil on canvas" },
      dimensions: { lt: "50 × 71 cm", en: "50 × 71 cm" },
    },
  ),
  // P1oR_cH names these eight works as one exhibition group, not individual titles.
  artwork(
    "arbata-ir-arbatinukai",
    "painting",
    { lt: "Arbata ir arbatinukai", en: "Tea and Teapots" },
    ["owner-tea-and-teapots-2024"],
    {
      year: "2024",
      technique: { lt: "Aliejus, drobė", en: "Oil on canvas" },
      dimensions: { lt: "50 × 70 cm", en: "50 × 70 cm" },
      description: {
        lt: "2024 m. parodos „Arbata ir arbatinukai“ darbai.",
        en: "Works from the 2024 exhibition ‘Tea and Teapots’.",
      },
      metadataReviewState: "complete",
    },
  ),
  // Source: 2026-09-18-13h21m43s-P1oSmWL-0J-9-paskutiniai tapybos darbai.eml.
  // All three JPEG attachments are retained together, in email order.
  artwork(
    "daiktai-nesantys-sekme",
    "painting",
    { lt: "Daiktai nešantys sėkmę", en: "Objects That Bring Good Luck" },
    ["b-0acc8d904eef"],
    {
      year: "2016",
      technique: { lt: "Aliejus, drobė", en: "Oil on canvas" },
      metadataReviewState: "complete",
    },
  ),
  artwork(
    "perkuno-zirgai",
    "painting",
    { lt: "Perkūno žirgai", en: "Perkūnas's Horses" },
    ["b-9acd13976f5d"],
    {
      year: "2020",
      technique: { lt: "Aliejus, drobė", en: "Oil on canvas" },
      dimensions: { lt: "70 × 60 cm", en: "70 × 60 cm" },
      description: {
        lt: "Dalyvavo parodoje: Dusetų dailės galerija – Kultūros centras.",
        en: "Participated in an exhibition at Dusetos Art Gallery – Cultural Centre.",
      },
      exhibitionLink: {
        url: "https://www.dusetukultura.lt/tradicine-tarptautine-paroda-zirgas-daileje-ir-katalogo-pristatymas/",
        label: {
          lt: "Dusetų dailės galerija – Kultūros centras",
          en: "Dusetos Art Gallery – Cultural Centre",
        },
      },
    },
  ),
  artwork(
    "pasakeles",
    "painting",
    { lt: "Pasakėlės", en: "Little Tales" },
    ["b-fc1bfa3ced64"],
    {
      year: "2021",
      technique: { lt: "Mišri technika, drobė", en: "Mixed media on canvas" },
      dimensions: { lt: "100 × 150 cm", en: "100 × 150 cm" },
      description: {
        lt: "Sukurti parodai „Menas turi atmintį“.",
        en: "Created for the exhibition “Art Has Memory”.",
      },
      exhibitionLink: {
        url: "https://zarasumuziejus.lt/menas-turi-atminti/",
        label: {
          lt: "Menas turi atmintį — Zarasų krašto muziejus",
          en: "Art Has Memory — Zarasai Regional Museum",
        },
      },
    },
  ),
  artwork(
    "sanciu-gatves",
    "painting",
    { lt: "Šančių gatvės", en: "Šančiai Streets" },
    ["b-6de0c9e53e20"],
    {
      year: "2013",
      technique: {
        lt: "Aliejus, drobė; triptikas",
        en: "Oil on canvas; triptych",
      },
      dimensions: { lt: "100 cm skersmuo", en: "100 cm diameter" },
    },
  ),
  // The email explicitly names this 2013 series. Photos document the series;
  // no individual artwork titles or photo-specific captions are established.
  // Source: am_web/mail_text/2026-09-23-19h00m13s-P2EQCvh--3-9-.txt.
  artwork(
    "moteris-farmacininke-tarpukaryje",
    "other",
    {
      lt: "Moteris farmacininkė tarpukaryje",
      en: "A Woman Pharmacist in the Interwar Period",
    },
    ["b-d2286a218388"],
    {
      year: "2013",
      technique: { lt: "Aliejinė tapyba, ciklas", en: "Oil painting, series" },
      materials: {
        lt: "Pakartotinai panaudoti tarpukario audiniai ir nosinaitės, seni rėmai ir porėmiai",
        en: "Reused interwar fabrics and handkerchiefs, old frames and stretchers",
      },
      description: {
        lt: "Tapybos ciklas, kuriame tarpukario suknelių audiniai ir nosinaitės susiūti į tapybos drobes, panaudoti seni rėmai ir porėmiai. Kasdieninių daiktų detalėmis autorė siekia perteikti to laikotarpio Čikagos atmosferą ir istoriją.",
        en: "A painting series using interwar dress fabrics and handkerchiefs stitched into painting surfaces, with reused frames and stretchers. Through details of everyday objects, the artist seeks to evoke the atmosphere and history of Chicago in that period.",
      },
    },
  ),
  artwork(
    "studiju-darbas-2010",
    "painting",
    { lt: "Studijų darbai", en: "Study works" },
    ["b-ac8c9e1e763d", "b-c43c800f7544"],
    {
      year: "2010",
      technique: { lt: "Aliejus", en: "Oil" },
    },
  ),
  artwork(
    "kiolnas",
    "drawings",
    { lt: "Kiolno eskizai", en: "Cologne Sketches" },
    ["b-ced4d9980459"],
    {
      year: "2007",
      technique: { lt: "Eskizai pieštuku", en: "Pencil sketches" },
      description: {
        lt: "2007, Kiolnas, eskizai pieštuku.",
        en: "2007, Cologne, pencil sketches.",
      },
      metadataReviewState: "complete",
    },
  ),
  // Complete PDF batch; provenance and reviewed rotations are indexed separately.
  artwork(
    "minges-eskizai",
    "drawings",
    { lt: "Mingės eskizai", en: "Mingė Sketches" },
    ["b-minge-2010"],
    {
      year: "2010",
      technique: { lt: "Mišri technika", en: "Mixed media" },
      description: {
        lt: "2010, Mingė, eskizai, mišri technika.",
        en: "2010, Mingė, sketches, mixed media.",
      },
      metadataReviewState: "complete",
    },
  ),
  artwork(
    "lietuviskos-kortos",
    "other",
    { lt: "Lietuviškos kortos", en: "Lithuanian Cards" },
    ["b-e1814c400625"],
    {
      year: "2014",
      technique: {
        lt: "Mišri technika, grafika",
        en: "Mixed media, graphic works",
      },
      dimensions: { lt: "10 × 10 cm", en: "10 × 10 cm" },
      description: {
        lt: "Grafikos darbai, sukurti pagal tarpukario būrimo kortų ženklus.",
        en: "Graphic works based on symbols from interwar fortune-telling cards.",
      },
    },
  ),
  // Owner-requested grouping/category, not an inference from the source email.
  // All ten source IDs are retained; no individual captions/dates are asserted.
  // Provenance: content/indexes/supplied_artwork_index.json.
  artwork("ivairus", "drawings", { lt: "Įvairūs", en: "Various" }, [
    "owner-P2RnMId",
  ]),
];
export const publishedArtworks = artworks.filter(
  (item) => item.publicationState === "published",
);
export function artworkMedia(artwork: Artwork): MediaAsset[] {
  return artwork.mediaIds.flatMap((id) => {
    const media = mediaById.get(id);
    return media ? [media] : [];
  });
}
export function artworkCover(artwork: Artwork): MediaAsset | undefined {
  return mediaById.get(artwork.coverMediaId);
}
export const projects = [
  {
    slug: "burtu-miskas",
    title: { lt: "Burtų miškas", en: "Burtų miškas (Forest of Spells)" },
    intro: {
      lt: "Meninė instaliacija ir renginys Tiltiškėse, 2023 m.",
      en: "An art installation and event in Tiltiškės, 2023.",
    },
    // Sources: am_web/mail_text/2026-08-22-08h44m20s-P-cQN5t--F-9-.txt;
    // am_web/attachments/about/2026-08-20-18h58m50s-P-VJqBH--B-9-apie mane__att-02.docx.
    // The land-art object is distinct from the ceramic triptych “Miškas”.
    body: {
      lt: "2023 m. lapkričio 3 d. Tiltiškėse surengtas vėlėms skirtas renginys su menine instaliacija „Burtų miškas“ — vieta pokalbiui su mirusiais. Tiltiškių „Burtų miškelyje“ esantis žemės meno objektas „Vėlių kalbos ženklai“ skirtas pažinti ir skaityti senuosius ženklus.",
      en: "An event for the departed on 3 November 2023 in Tiltiškės included the installation “Forest of Spells” — a place for a conversation with the dead. The land-art object “Vėlių kalbos ženklai” (Signs of the Language of the Departed) in Tiltiškės' Forest of Spells invites people to discover and read ancient signs.",
    },
    // Project media has its own source-backed membership, independent of works.
    mediaIds: mediaForBatches("b-14f7530c84cc", "b-4185b6c84e45"),
  },
  {
    slug: "am-ukis",
    title: { lt: "A.M. ūkis", en: "A.M. ūkis" },
    // Source: am_web/attachments/about/2026-08-20-18h58m50s-P-VJqBH--B-9-apie mane__att-01.doc.
    // Historical/creative context only; no current visiting or service arrangements.
    intro: {
      lt: "Meno terapijos sodyba Tiltiškėse, Zarasų krašte, įkurta 2021 m.",
      en: "An art-therapy homestead in Tiltiškės, in the Zarasai region, established in 2021.",
    },
    body: {
      lt: "Aušra Mačiulytė 2021 m. Tiltiškių kaime, Zarasų rajone, įkūrė meno terapijos sodybą. Ši vieta siejasi su autorės kūryba — keramika, tapyba ir žemės menu — bei meno terapijos praktika.",
      en: "In 2021, Aušra Mačiulytė established an art-therapy homestead in the village of Tiltiškės, Zarasai district. The place is connected with her creative work — ceramics, painting and land art — and her art-therapy practice.",
    },
    mediaIds: [],
  },
  {
    slug: "baltiskas-masazas",
    title: { lt: "Baltiškas masažas", en: "Baltic massage" },
    intro: {
      lt: "Autorės veiklos kryptis.",
      en: "A direction within the artist's practice.",
    },
    body: {
      lt: "Autorės pateiktame tekste baltiškasis masažas aprašomas kaip technika, sukurta pagal lietuviškas etnografines grožio procedūras ir pirties tradicijas. Tekste kūrėjomis įvardytos Aušra Mačiulytė ir gydytoja Renata Kielaitė.",
      en: "In the artist's supplied text, Baltic massage is described as a technique developed from Lithuanian ethnographic beauty practices and sauna traditions. The text names Aušra Mačiulytė and physician Renata Kielaitė as its creators.",
    },
    mediaIds: [],
  },
  {
    slug: "lietuviskos-runos",
    title: { lt: "Lietuviškos runos", en: "Lithuanian Runes" },
    // Source: am_web/mail_text/2026-09-24-13h56m51s-P2IUMi4--3-9-Re_ Lietuviškos runos.txt
    intro: {
      lt: "Autorės pateiktame tekste pristatoma būrimo lietuviškomis runomis sistema, pateikta žaidimo forma.",
      en: "The author's supplied text presents a divination system using Lithuanian runes in a game format.",
    },
    body: {
      lt: "Pagal autorės aprašą, žaidimo forma skirta padėti įsiminti ženklų reikšmes. Tekste pateikiami jų naudojimo mezginiuose, baldų dekoravime ir papuošaluose pavyzdžiai.",
      en: "According to the author's description, the game format is intended to help remember the symbols' meanings. The text gives examples of using them in knitting, furniture decoration and jewellery.",
    },
    mediaIds: [],
  },
] as const;
export const publications = [
  {
    slug: "straipsnis-2001",
    title: {
      lt: "Pokalbis su šiuolaikine ragana, arba Niekada nesakyk niekada",
      en: "Pokalbis su šiuolaikine ragana, arba Niekada nesakyk niekada",
    },
    credit: "Rita Šemelytė · Vyras ir moteris · 2001/3 · 38–39",
    pdfIds: ["article-2001-1", "article-2001-2"],
    summary: {
      lt: "Šiame interviu būrėja Aušra pasakoja apie jaunystėje bandytus meilės burtus ir teigia vėliau supratusi, kad mėginimas pajungti kitą žmogų savo valiai turi skaudžių pasekmių. Ji kalba apie su meile kuriamus audinius ir mezginius, jų, jos manymu, apsauginę galią, iš močiutės perimtas žinias apie žoles ir užkalbėjimus. Pašnekovė aptaria vaizduotės bei tikėjimo reikšmę, o kalbėdama apie sėkmę ir pinigus labiau vertina gebėjimą užsidirbti nei netikėtą laimėjimą ir pabrėžia kryptingų minčių svarbą.",
      en: "In this interview, fortune-teller Aušra recounts experimenting with love spells in her youth and says she later came to believe that trying to bend another person to her will has painful consequences. She discusses fabrics and knitwear made with love, their protective power in her view, and knowledge of herbs and incantations inherited from her grandmother. The interviewee reflects on imagination and faith; when discussing success and money, she values the ability to earn a living over an unexpected windfall and emphasises focused thought.",
    },
    preview: {
      url: publication2001Preview,
      sourceId: "article-2001-1",
      width: 1200,
      height: 1037,
      alt: {
        lt: "Originalaus 38 puslapio iškarpa: rubrika „Naujoji moteris · mistika“, straipsnio antraštė, iliustracija ir įžanga",
        en: "Crop of original page 38: the ‘Naujoji moteris · mistika’ section heading, article title, illustration and introduction",
      },
      caption: {
        lt: "Originalaus 38 puslapio iškarpa",
        en: "Detail from original page 38",
      },
    },
  },
  // Corrected issue/page selection: docs/DRAUGAS_SOURCE_CORRECTION_20261014.md.
  {
    slug: "draugas-2013-08-24",
    title: {
      lt: "Dvigubos iškilmės: prisimintas kun. dr. K. Trimakas ir Žolinės",
      en: "Dvigubos iškilmės: prisimintas kun. dr. K. Trimakas ir Žolinės",
    },
    credit:
      "Edvardas Šulaitis · Draugas · 2013-08-24 · 4 · Jono Kuprio nuotraukos",
    pdfIds: ["draugas-2013-08-24"],
    summaryTitle: { lt: "Straipsnio santrauka", en: "Article summary" },
    summary: {
      lt: "Edvardas Šulaitis aprašo Cicero lietuvių susibūrimą po šv. Mišių: prisimintas neseniai miręs kun. dr. Kęstutis Trimakas, sukalbėta malda už mirusįjį ir paminėta Žolinė. Susirinkusiesiems pristatytas mainų programos dalyvis Rokas Keršys, atvykęs iš Kauno jėzuitų gimnazijos mokytis Čikagoje. Straipsnyje taip pat pranešama, kad kun. Antanas Gražulis patvirtintas nuolatiniu Cicero lietuvių tikinčiųjų kunigu.",
      en: "Edvardas Šulaitis describes a gathering of Cicero Lithuanians after Mass: the recently deceased Fr. Dr. Kęstutis Trimakas was remembered, prayers were said for him and Žolinė, the Feast of the Assumption, was celebrated. Exchange student Rokas Keršys, who came from the Kaunas Jesuit school to study in Chicago, was introduced to the gathering. The article also reports that Fr. Antanas Gražulis was confirmed as the regular priest for Cicero's Lithuanian Catholic community.",
    },
    originalsNote: {
      lt: "Pateikiamas visas originalus šio „Draugo“ numerio 4 puslapis su straipsniu „Dvigubos iškilmės: prisimintas kun. dr. K. Trimakas ir Žolinės“ ir Jono Kuprio nuotraukomis. Visame puslapyje yra ir atskiras pranešimas, kuris nėra šio straipsnio dalis. Spustelėkite vaizdą, kad atvertumėte visą puslapį; PDF nuoroda pateikia visą 16 puslapių laikraščio numerį.",
      en: "Shown here is complete original page 4 of this Draugas issue, with ‘Dvigubos iškilmės: prisimintas kun. dr. K. Trimakas ir Žolinės’ and photographs by Jonas Kuprys. The full page also contains a separate report that is not part of this article. Open the image to view the full page; the PDF link opens the complete 16-page newspaper issue.",
    },
    preview: {
      url: draugasAugustPreview,
      sourceId: "draugas-2013-08-24",
      width: 1200,
      height: 674,
      alt: {
        lt: "Originalaus 4 puslapio iškarpa: „Dvigubos iškilmės: prisimintas kun. dr. K. Trimakas ir Žolinės“ antraštė ir Jono Kuprio nuotraukos",
        en: "Crop of original page 4: the headline ‘Dvigubos iškilmės: prisimintas kun. dr. K. Trimakas ir Žolinės’ and photographs by Jonas Kuprys",
      },
      caption: {
        lt: "Originalaus 4 puslapio iškarpa · Jono Kuprio nuotraukos",
        en: "Detail from original page 4 · photographs by Jonas Kuprys",
      },
    },
  },
  {
    slug: "draugas-2013-09-28",
    title: {
      lt: "Sveikata tokia pat užkrečiama kaip ir liga",
      en: "Sveikata tokia pat užkrečiama kaip ir liga",
    },
    credit: "Raimundas Marius Lapas · Draugas · 2013-09-28 · 10–11",
    pdfIds: ["draugas-2013-09-28", "draugas-2013-09-28-page-11"],
    summaryTitle: { lt: "Straipsnio santrauka", en: "Article summary" },
    summary: {
      lt: "Raimundas Marius Lapas pasakoja apie Čikagos Bridgeporto lietuvių istoriją ir vaistininkę Jadvygą Lesčiauskaitę (Harriet Lescauskas). Po paantraštės „Visi keliai veda į ...Bridgeportą?“ autorius kalbina Aušrą Mačiulytę apie išsaugotą „Bridgeport Pharmacy“, jos archyvą ir sumanymą rengti parodas buvusios vaistinės patalpose. Aušra pasakoja apie Harriet gyvenimą, darbą ir pagalbą žmonėms; pokalbis tęsiamas ir baigiamas 11 puslapyje.",
      en: "Raimundas Marius Lapas discusses the Lithuanian history of Chicago's Bridgeport neighbourhood and pharmacist Jadvyga Lesčiauskaitė (Harriet Lescauskas). After the subheading ‘Visi keliai veda į ...Bridgeportą?’ he interviews Aušra Mačiulytė about the preserved Bridgeport Pharmacy, its archive and the idea of holding exhibitions in the former pharmacy. Aušra describes Harriet's life, work and help for others; the interview continues and concludes on page 11.",
    },
    originalsNote: {
      lt: "Pateikiami originalūs šio „Draugo“ numerio 10 ir 11 puslapiai; spausdinti puslapių numeriai sutampa su PDF puslapiais. Pokalbis su Aušra Mačiulyte prasideda 10 puslapyje po paantraštės „Visi keliai veda į ...Bridgeportą?“ ir tęsiamas 11 puslapyje, kuriame jos portretas pažymėtas „V. Matranga nuotr.“. 10 puslapyje vaistinės pastato nuotrauka pažymėta „Jono Kuprio nuotr.“, o istorinė nuotrauka – „Šeimos archyvo nuotr.“. Spustelėkite vaizdą, kad atvertumėte visą puslapį; PDF nuoroda pateikia visą 16 puslapių laikraščio numerį.",
      en: "Shown here are original pages 10 and 11 of this Draugas issue; the printed page numbers match the PDF pages. The interview with Aušra Mačiulytė begins on page 10 after ‘Visi keliai veda į ...Bridgeportą?’ and continues on page 11, where her portrait is credited ‘V. Matranga nuotr.’. On page 10, the pharmacy building photograph is credited to Jonas Kuprys and the historical photograph to the family archive. Open an image to view the full page; the PDF link opens the complete 16-page newspaper issue.",
    },
    preview: {
      url: draugasSeptemberPreview,
      sourceId: "draugas-2013-09-28",
      width: 1200,
      height: 1110,
      alt: {
        lt: "Originalaus 10 puslapio iškarpa: „Sveikata tokia pat užkrečiama kaip ir liga“ antraštė, vaistinės nuotraukos, Jadvygos Lesčiauskaitės biografija ir autoriaus Raimundo Mariaus Lapo vardas",
        en: "Crop of original page 10: the headline ‘Sveikata tokia pat užkrečiama kaip ir liga’, pharmacy photographs, Jadvyga Lesčiauskaitė's biography and Raimundas Marius Lapas's byline",
      },
      caption: {
        lt: "Originalaus 10 puslapio iškarpa",
        en: "Detail from original page 10",
      },
    },
  },
] as const;
export function localized(
  value: Localized | undefined,
  locale: Locale,
): string | undefined {
  return value?.[locale] || value?.lt;
}
